import { useEffect, useRef, useState } from "react";
import type { ProductCardBlock } from "@/types/content";
import type { ProductVariant } from "@/types/product";
import { useCart } from "@/hooks/useCart";
import { trackAddToCart } from "@/components/analytics/gtm-events";
import { useAnalyticsIdentity } from "@/components/analytics/AnalyticsIdentityContext";

interface UseProductAddToCartOptions {
  /** GA4 list the add is reported under (`item_list_name`), e.g. a My Account module. */
  listName?: string;
  /** Runs after a successful add, once `add_to_cart` has been sent. */
  onAdded?: (selectedVariant?: ProductVariant) => void;
}

/**
 * A product card's add-to-cart: straight in for a single-version product or a
 * bundle, through a version list for a product sold in several versions, then a
 * brief "Added!" and the store's `add_to_cart` event. Shared by the storefront
 * ProductCard and the My Account recommendation tiles, so both buy the same way.
 *
 * Adding never leaves the page: CartContext opens the mini cart itself.
 */
export function useProductAddToCart(
  block: ProductCardBlock,
  options: UseProductAddToCartOptions = {}
) {
  const { listName, onAdded } = options;
  const { addToCart, addBundleToCart, isAddingToCart } = useCart();
  const identity = useAnalyticsIdentity();
  const [isAdded, setIsAdded] = useState(false);
  const [isVariantPickerOpen, setIsVariantPickerOpen] = useState(false);
  const cartAreaRef = useRef<HTMLDivElement>(null);

  const variants = block.variants ?? [];
  const hasVariants = variants.length > 0;

  // A bundle is bought through its own endpoint, which explodes it into one line per bundled
  // item — the ordinary add-to-cart endpoint rejects it outright. When one of those items is
  // sold in several versions the choice cannot be made on a card this small, so the bundle is
  // added with none chosen: each such line gets its own picker in the cart, and checkout stays
  // blocked until every one is resolved.
  const isBundle = block.isBundle ?? false;

  // Dismiss the version list the way any popover is expected to behave.
  useEffect(() => {
    if (!isVariantPickerOpen) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!cartAreaRef.current?.contains(event.target as Node)) {
        setIsVariantPickerOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsVariantPickerOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isVariantPickerOpen]);

  const handleAddToCart = async (selectedVariant?: ProductVariant) => {
    if (isAddingToCart || isAdded || !block.productReference) return;
    // A product sold in several versions is only ever added through the picker.
    if (hasVariants && !selectedVariant) return;

    const success = isBundle
      ? await addBundleToCart(block.productReference, 1)
      : await addToCart(block.productReference, 1, selectedVariant?.id);

    if (success) {
      setIsVariantPickerOpen(false);
      setIsAdded(true);
      setTimeout(() => setIsAdded(false), 2000);

      const calculatedPrice = block.priceInfo?.calculatedPrice ?? 0;
      trackAddToCart({
        product: block,
        currency: block.priceInfo?.currency ?? "",
        price: calculatedPrice,
        value: calculatedPrice,
        quantity: 1,
        variantSku: selectedVariant?.sku ?? undefined,
        ...(listName && { listName }),
        identity,
      });
      onAdded?.(selectedVariant);
    }
  };

  /** Variant products open the version list; everything else adds straight away. */
  const handleAddButtonClick = () => {
    if (hasVariants) {
      setIsVariantPickerOpen((open) => !open);
      return;
    }
    void handleAddToCart();
  };

  return {
    variants,
    hasVariants,
    isAddingToCart,
    isAdded,
    isVariantPickerOpen,
    cartAreaRef,
    handleAddToCart,
    handleAddButtonClick,
  };
}
